import { useCallback, useContext } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Condition, IJourneyStepNavigation, IReinstateablePolicy, ValueOrArray } from "config";
import { ConditionKey } from "config/conditions";
import { useCondition } from "modules";
import { JourneySectionContext } from "modules/managers/JourneySectionProvider";
import { RootState, useAppDispatch } from "store";
import { getEligibleForCoverageUnderwriting } from "store/actions/account";
import {
  clearJourneyActive,
  finishJourney,
  setJourneyInactive,
  setJourneyStep,
  setShowJourneyProgress,
  startJourney as startJourneyAction,
} from "store/actions/journey";
import { getProductOfferings } from "store/actions/product-offerings";
import { scrollToTop } from "utils/scrollToTop";

import {
  compact,
  every,
  filter,
  find,
  findIndex,
  first,
  flatten,
  get,
  isEmpty,
  isEqual,
  isFunction,
  last,
  some,
} from "lodash-es";
import store2 from "store2";

const { local } = store2;

const parseBackNavMetadata = (
  backNavMetadata: boolean | ValueOrArray<IJourneyStepNavigation> | undefined,
  conditions: Condition
) => {
  const flattenedBackNav = compact(flatten([backNavMetadata]));
  const filteredBackNavMetadata = find(flattenedBackNav, meta => {
    if (typeof meta === "boolean") {
      return true;
    }

    const stepConditions = compact(flatten([meta.conditions]));
    if (isEmpty(stepConditions)) {
      return true;
    }

    const allConditionsMet = every(stepConditions, condition =>
      get(conditions, condition as ConditionKey, false)
    );
    return allConditionsMet;
  });

  return filteredBackNavMetadata;
};

export const useJourney = <TData = { [key: string]: unknown }>() => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const journey = useSelector((state: RootState) => state.journey);
  const userState = useSelector((state: RootState) => state.user);
  const anyJourneysActive = !isEmpty(journey.activeJourneys);
  const currentStepKey = journey.steps.active;
  const currentStep = get(journey.steps.collection, currentStepKey);
  const { activateNextSection, isLastSectionActive } = useContext(JourneySectionContext);
  const conditions = useCondition({});

  const userId = userState.id;

  const startJourney = useCallback(
    (
      journeyId: string,
      context: {
        startingStep?: string;
        data?: Record<string, unknown>;
        metadata?: Record<string, unknown>;
      } = {}
    ) => {
      const isJourneyActive =
        some(journey.activeJourneys, journeyId) ||
        isEqual(get(journey.collection, [journeyId, "active"]), true);
      if (isJourneyActive) {
        return;
      }

      const { startingStep, data, metadata } = context;
      const contextData = data || {};
      scrollToTop({ behavior: "auto" });
      dispatch(startJourneyAction(journeyId, false, { data: contextData, metadata }, navigate));
      if (startingStep && !isEmpty(startingStep)) {
        dispatch(setJourneyStep(startingStep));
      }
    },
    [dispatch, journey.activeJourneys, journey.collection, navigate]
  );

  const startFulfillmentJourney = useCallback(async () => {
    await dispatch(getProductOfferings());
    const response = await dispatch(getEligibleForCoverageUnderwriting());
    const hasPolicy = get(response, "payload.hasPolicy", false);

    if (hasPolicy) {
      navigate("/", { replace: true });
    } else {
      startJourney("fulfillment");
      dispatch(setShowJourneyProgress(true));
    }
  }, [navigate, startJourney, dispatch]);

  const startReinstatementFulfillmentJourney = useCallback(
    async (reinstateablePolicyId: IReinstateablePolicy["id"]) => {
      startJourney("reinstatementFulfillment", {
        metadata: { flowScope: "reinstatement", reinstateablePolicyId },
      });
      dispatch(setShowJourneyProgress(true));
    },
    [startJourney, dispatch]
  );

  const previousStepCallback = useCallback(
    (fc?: (...args: unknown[]) => unknown) => {
      return (...args: unknown[]) => {
        scrollToTop({ behavior: "auto" });

        if (!anyJourneysActive) {
          if (isFunction(fc)) {
            fc(args);
          }
        } else {
          const latestJourneyId = last(journey.activeJourneys) || "";
          const latestJourney = get(journey.collection, latestJourneyId);

          const currentStepIndex = findIndex(latestJourney.steps, val =>
            isEqual(val, currentStepKey)
          );

          const backNavMetadata = currentStep?.navigation?.back;
          const targetBackNavMetadata = parseBackNavMetadata(backNavMetadata, conditions);
          const targetStepKey =
            typeof targetBackNavMetadata === "boolean" ? undefined : targetBackNavMetadata?.key;
          const previousStepKey = targetStepKey || latestJourney.steps[currentStepIndex - 1];
          const previousStep = find(journey.steps.collection, step => {
            return isEqual(previousStepKey, step.id) || isEqual(previousStepKey, step.key);
          });

          const previousStepType = get(previousStep, "type");
          const isFirstStepInJourney = isEqual(currentStepKey, first(latestJourney.steps));

          if (previousStepType === "journey" && !isFirstStepInJourney) {
            dispatch(startJourneyAction(previousStepKey, true));
          } else {
            if (isFirstStepInJourney) {
              const isJourneyNested = !isEmpty(latestJourney.parent);
              const parentJourney = get(journey.collection, latestJourney?.parent || "");
              const parentJourneyIndex = findIndex(get(parentJourney, "steps"), item =>
                isEqual(item, latestJourneyId)
              );
              const nextParentJourneyStep = parentJourney?.steps[parentJourneyIndex - 1];

              if (isJourneyNested) {
                dispatch(setJourneyInactive(latestJourneyId));
                dispatch(setJourneyStep(nextParentJourneyStep));
              } else {
                return;
              }
            } else {
              dispatch(setJourneyStep(previousStepKey));
            }
          }
        }
      };
    },
    [
      anyJourneysActive,
      journey.activeJourneys,
      journey.collection,
      journey.steps.collection,
      currentStep,
      currentStepKey,
      conditions,
      dispatch,
    ]
  );

  const nextStepCallback = useCallback(
    (
      fc: ((...args: unknown[]) => unknown) | undefined = undefined,
      concurrent = true,
      skipSection = false
    ) => {
      return async (...args: unknown[]) => {
        scrollToTop({ behavior: "auto" });

        if (concurrent || !anyJourneysActive) {
          if (isFunction(fc)) {
            await fc(args);
          }
        }
        if (anyJourneysActive) {
          const latestJourneyId = last(journey.activeJourneys) as string;
          const latestJourney = get(journey.collection, latestJourneyId);
          const latestJourneySteps = filter(latestJourney.steps, stepKey => {
            const step = get(journey.steps.collection, stepKey);
            const stepConditions = flatten([step.conditions]);
            if (isEmpty(stepConditions)) {
              return true;
            }
            const allConditionsMet = every(stepConditions, condition =>
              get(conditions, condition as ConditionKey, false)
            );
            return !allConditionsMet;
          });

          const currentStepIndex = findIndex(latestJourneySteps, val =>
            isEqual(val, currentStepKey)
          );
          const nextStepKey = latestJourneySteps[currentStepIndex + 1];
          const nextStep = find(journey.steps.collection, step => {
            return isEqual(nextStepKey, step.id) || isEqual(nextStepKey, step.key);
          });

          const nextStepType = get(nextStep, "type");

          const stepIsTerminal = get(currentStep, "terminal", false);
          const isLastStepInJourney =
            isEqual(currentStepKey, last(latestJourneySteps)) || isEqual(stepIsTerminal, true);

          if (nextStepType === "journey" && !isLastStepInJourney) {
            dispatch(startJourneyAction(nextStepKey, true));
          } else {
            if (isLastStepInJourney) {
              const isJourneyNested = !isEmpty(latestJourney.parent);
              const parentJourney = get(journey.collection, latestJourney.parent as string);
              const parentJourneyIndex = findIndex(get(parentJourney, "steps"), item =>
                isEqual(item, latestJourneyId)
              );

              const nextParentJourneyStep = parentJourney?.steps[parentJourneyIndex + 1];

              if (isJourneyNested) {
                dispatch(setJourneyInactive(latestJourneyId));
                dispatch(setJourneyStep(nextParentJourneyStep));
              } else {
                if (isEqual(currentStep.type, "section")) {
                  const { success } = activateNextSection();
                  if (!success) {
                    dispatch(finishJourney({ id: latestJourneyId, navigate }));
                  }
                } else {
                  dispatch(finishJourney({ id: latestJourneyId, navigate }));
                }
              }
            } else {
              if (isEqual(currentStep.type, "section") && !isLastSectionActive && !skipSection) {
                const { success } = activateNextSection();
                if (!success) {
                  dispatch(setJourneyStep(nextStepKey));
                }
              } else {
                dispatch(setJourneyStep(nextStepKey));
              }
            }
          }
        }
      };
    },
    [
      activateNextSection,
      anyJourneysActive,
      conditions,
      currentStep,
      currentStepKey,
      dispatch,
      isLastSectionActive,
      journey.activeJourneys,
      journey.collection,
      journey.steps.collection,
      navigate,
    ]
  );

  const buildNavigationMetadata = () => {
    const navigation = currentStep?.navigation;
    const shouldExit = navigation?.exit === true;
    const backNavMetadata = navigation?.back;
    const targetBackNavMetadata = parseBackNavMetadata(backNavMetadata, conditions);

    const backNavProps = {
      replaceExitInJourneyWithBack:
        !shouldExit && (backNavMetadata === true || !!targetBackNavMetadata),
    };

    return backNavProps;
  };
  const navigationMetadata = buildNavigationMetadata();

  const rootJourney = find(
    journey.collection,
    item => item?.parent === undefined && item.active === true
  );

  const exitJourney = useCallback(() => {
    const storage = isEmpty(userId) ? local : local.namespace(userId as string);
    // Remove from localStorage FIRST so JourneyLocalSync cannot re-write the
    // progress entry between the remove and the Redux state update.
    storage.remove("journeyProgress");
    dispatch(clearJourneyActive());
  }, [dispatch, userId]);

  const rootJourneyData = (rootJourney?.data || {}) as TData;

  return {
    navigationMetadata,
    anyJourneysActive,
    currentStep,
    startJourney,
    startFulfillmentJourney,
    startReinstatementFulfillmentJourney,
    nextStepCallback,
    previousStepCallback,
    exitJourney,
    rootJourney,
    data: rootJourneyData,
  };
};
